import { authClient } from '@/lib/auth-client';
import type { Session } from 'better-auth';

interface UserSession {
  id: string;
  userId: string;
  expiresAt: string;
  createdAt: string;
  ipAddress?: string;
  userAgent?: string;
  location?: string;
  current?: boolean; // Flag to indicate if this is the current session
}

/**
 * Session Service
 * Handles user session management operations
 */
export const sessionService = {
  /**
   * Get all active sessions for the current user
   * @returns Array of user sessions
   */
  async getSessions(): Promise<UserSession[]> {
    try {
      // Get current session to identify which one is active
      const { data: currentSession } = await authClient.getSession({});
      
      // For Better Auth, we typically use the admin API to manage sessions
      // But for user self-management, we might need a custom API endpoint
      // For now, we'll fetch sessions through a custom endpoint
      const response = await fetch('/api/sessions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch sessions');
      }

      const data = await response.json();
      const sessions: UserSession[] = data.sessions || [];

      // Mark the current session
      if (currentSession?.session) {
        return sessions.map(session => ({
          ...session,
          current: session.id === currentSession.session.id
        }));
      }
      
      return sessions;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  },

  /**
   * Revoke a specific session by token
   * @param sessionToken The session token to revoke
   * @returns Promise that resolves when the session is revoked
   */
  async revokeSession(sessionToken: string): Promise<void> {
    try {
      const response = await fetch(`/api/sessions/${sessionToken}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to revoke session');
      }
    } catch (error) {
      console.error('Error revoking session:', error);
      throw error;
    }
  },

  /**
   * Revoke all sessions for the current user except the current one
   * @returns Promise that resolves when other sessions are revoked
   */
  async revokeAllOtherSessions(): Promise<void> {
    try {
      const response = await fetch('/api/sessions/revoke-all-others', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to revoke other sessions');
      }
    } catch (error) {
      console.error('Error revoking other sessions:', error);
      throw error;
    }
  },
};